import {
  BOOK_TRIAL_CLASS_REF,
  REGISTER_REF,
  SIGNUP_SOURCE_LOCAL_STORAGE_KEY,
} from '@common/constants'
import RatelimitErrorModal from '@components/auth/ratelimit-error-modal.dynamic'
import BookTrialClassForm from '@components/book-trial-class/form'
import AuthModal from '@containers/competitions/auth-modal.dynamic'
import { UserProps, useSessionContext } from '@contexts/session'
import useSegment, {
  getSegmentTrackingDict,
  SegmentEventEnum,
} from '@hooks/useSegment'
import { ROLES } from '@lib/enums/dashboard'
import { FB_EVENTS } from '@lib/enums/tracking'
import { GoogleAdsConversions } from '@lib/analytics/gtag'
import {
  getMixpanelSubject,
  MixpanelEvent,
  MixpanelProperties,
  trackMixpanelEvent,
  trackMixpanelSignupCompleted,
} from '@lib/analytics/mixpanel'
import { generateClickIDParametersDict } from '@lib/analytics/track-utm'
import {
  normalizeEmailForTracking,
  normalizePhoneForTracking,
} from '@lib/utils/tracking'
import { isCountryBlockedForSignup } from '@lib/utils/country'
import {
  detectIdentifierMode,
  IdentifierMode,
  isPlaceholderPhone,
} from '@lib/utils/identifier'
import { getFromLocalStorage } from '@lib/utils/local-storage'
import { getValidatedSignupSourceFromQuery } from '@lib/utils/signup-source'
import {
  decodePhoneQueryValue,
  encodePhoneQueryValue,
} from '@lib/utils/phone-query'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import React, { FormEvent, useEffect, useState } from 'react'
import { USER_COUNTRY_FROM_IP_COOKIE } from '@lib/constants/common'
import { ENGLISH_CONTENT } from '@lib/constants/geo-contents/english'
import useLazyRecaptcha from '@hooks/useLazyRecaptcha'
import LazyRecaptcha from '@components/common/lazy-recaptcha'
import { LinguiJSMessageDescriptor } from '@lib/types/common'
import { msgT } from '@lib/utils/i18n'
import { t } from '@lingui/macro'
import { GRADES, GradeType } from '@components/common/forms/grade-select'
import { getTrialSubject } from '@lib/constants/booking'

const SUBJECT_OPTIONS = [
  { label: 'Coding', slug: 'coding' },
  { label: 'Math', slug: 'math-trial-class' },
  { label: 'Public speaking', slug: 'public-speaking-trial-class' },
]

const {
  heroFold: {
    form: {
      gradeFieldPlaceholder: DEFAULT_GRADE_PLACEHOLDER,
      phoneFieldLabel: DEFAULT_PHONE_LABEL,
    },
  },
} = ENGLISH_CONTENT

type Props = {
  next?: string
  cta?: string | LinguiJSMessageDescriptor
  openOtpVerificationModal?: boolean
  handleOtpVerified?: () => Promise<any>
  countryCode?: string
  shouldHaveFormShadow?: boolean
  formHeading?: string
  phoneFieldLabel?: string | LinguiJSMessageDescriptor
  gradeFieldPlaceholder?: string | LinguiJSMessageDescriptor
  termsAndConditions?: string
  isRightToLeftPage?: boolean
  trialCourseUrl?: string
  slug?: string
  trialSubject?: string
  subjectDropdown?: boolean
  utmSource?: string
  sourceOverride?: string
  // Opt-in to the combined phone-or-email field. Off everywhere by default, so
  // landing pages keep their phone-only form until their region gate lands.
  allowEmail?: boolean
}

type Query = {
  ph?: string
  source: string
  next?: string
  course?: string
  slug?: string
  utm_source?: string
}

declare global {
  interface Window {
    grecaptcha: any
  }
}

const BookTrialClassContainer = ({
  next,
  handleOtpVerified,
  openOtpVerificationModal = false,
  countryCode,
  shouldHaveFormShadow,
  formHeading = '',
  phoneFieldLabel = DEFAULT_PHONE_LABEL,
  gradeFieldPlaceholder = DEFAULT_GRADE_PLACEHOLDER,
  cta,
  termsAndConditions,
  isRightToLeftPage,
  trialCourseUrl,
  slug,
  trialSubject = 'Coding',
  subjectDropdown = false,
  utmSource,
  sourceOverride,
  allowEmail = false,
}: Props) => {
  const [selectedSubjectSlug, setSelectedSubjectSlug] = useState('')
  const [subjectError, setSubjectError] = useState('')

  const handleSubjectChange = (slug: string) => {
    setSelectedSubjectSlug(slug)
    setSubjectError('')
  }
  const router = useRouter()
  const { loggedIn, name, email, gender, phone, student } = useSessionContext()
  const track = useSegment()
  const [isAutoRegister, setIsAutoRegister] = useState(false)
  const LOGGEDIN_PROMO_PAGE = loggedIn && next ? true : false

  const [trialBookingData, setTrialBookingData] = useState({
    // User form inputs. `phone` holds whichever identifier was typed - a phone
    // number (dial code, no plus) or an email address; `identifierMode` says
    // which, and stays 'phone' unless `allowEmail` is on.
    phone: LOGGEDIN_PROMO_PAGE ? phone : '',
    identifierMode: 'phone' as IdentifierMode,
    name: LOGGEDIN_PROMO_PAGE ? name : 'Student',
    email: LOGGEDIN_PROMO_PAGE ? email : '',
    role: ROLES.STUDENT,
    childName: '',
    childGrade: {
      value: LOGGEDIN_PROMO_PAGE ? student?.grade : 'select',
      label: LOGGEDIN_PROMO_PAGE ? student?.grade : msgT(gradeFieldPlaceholder),
    },
    childGender: {
      value: LOGGEDIN_PROMO_PAGE ? gender : null,
      label: LOGGEDIN_PROMO_PAGE ? gender : 'Unspecified',
    },
    // Set only when an explicit answer arrives via ?hasLaptop=true|false (e.g.
    // from web-static). When undefined, prepareRegistrationFormBody falls back
    // to seeding from the device.
    hasLaptop: undefined as boolean | undefined,
    termsAndConditions: true,
    whatsappEnabled: true,
    signupSource:
      (typeof window !== 'undefined' &&
        getFromLocalStorage(SIGNUP_SOURCE_LOCAL_STORAGE_KEY)) ||
      router.asPath.split('?')[0],

    // Server response
    response: {},
    errors: {},
    loading: false,
  })

  const [showRatelimitModal, setShowRatelimitModal] = useState(false)
  const [isOtpVerificationModalOpen, setIsOtpVerificationModalOpen] = useState(
    false
  )
  const [activeRecaptchaFlow, setActiveRecaptchaFlow] = useState<
    'signup' | 'login'
  >('signup')
  const {
    recaptchaAPI,
    setShouldRecaptchaLoad,
    shouldRecaptchaLoad,
    setRecaptchaAPI,
  } = useLazyRecaptcha()

  const source = sourceOverride
    ? sourceOverride
    : router.pathname === '/register'
    ? REGISTER_REF
    : BOOK_TRIAL_CLASS_REF

  // slug always stays as the page slug (e.g. ezyschooling)
  const effectiveSlug = slug

  // When subjectDropdown is active, the selected subject slug is the course
  const course =
    subjectDropdown && selectedSubjectSlug
      ? selectedSubjectSlug
      : trialCourseUrl || null

  const getMixpanelFormProperties = (): MixpanelProperties => ({
    // eslint-disable-next-line @typescript-eslint/camelcase
    entry_source: source,
    subject:
      subjectDropdown && !selectedSubjectSlug
        ? undefined
        : getMixpanelSubject(course || trialSubject),
    course: course || undefined,
    // eslint-disable-next-line @typescript-eslint/camelcase
    identifier_type: trialBookingData.identifierMode,
  })

  const trackSignupFailed = (
    reason: string,
    statusCode?: number,
    errorBody?: unknown
  ) =>
    trackMixpanelEvent(MixpanelEvent.REGISTER_REQUEST_ERROR, {
      ...getMixpanelFormProperties(),
      reason,
      // eslint-disable-next-line @typescript-eslint/camelcase
      status_code: statusCode,
      // Names of the fields the API rejected, never their values.
      fields:
        errorBody && typeof errorBody === 'object'
          ? Object.keys(errorBody as Record<string, unknown>).join(',')
          : undefined,
    })

  const changeTrialBookingData = (data) => {
    setTrialBookingData((prevData) => ({ ...prevData, ...data }))
    // Any change in trialBookingData will trigger loading of reCaptcha
    setShouldRecaptchaLoad(true)
  }

  const handlePhoneInputChange = (value: string, mode: IdentifierMode) => {
    const identifierMode = allowEmail ? mode : 'phone'
    changeTrialBookingData({
      // `phone` holds the raw typed value in both modes - it is what the single
      // input renders. The request body is derived from it at submit time.
      phone: value,
      identifierMode,
      // Phone signups have never collected a real email, so one is synthesised
      // from the number. An email signup gives us the real thing.
      email:
        identifierMode === 'email'
          ? value.trim()
          : `${value}@codingal-student.com`,
    })
  }

  const handleSelectChange = (
    id: string,
    value: {
      value: string
      label: string
    }
  ) => {
    changeTrialBookingData({
      [id]: value,
    })
  }

  // const handleCheckboxChange = (elem: FormEvent<HTMLInputElement>): void => {
  //   changeTrialBookingData({
  //     [elem.currentTarget.name]: elem.currentTarget.checked,
  //   })
  // }

  // handle OTP request reCAPTCHA trigger here
  const handleOtpRequestCaptcha = async (recaptchaKey: string) => {
    // If the reCAPTCHA code is null or undefined indicating that
    // the reCAPTCHA was expired then return early
    if (!recaptchaKey) return

    const authUtils = await import('@containers/auth/utils')

    const formBody = authUtils.prepareRegistrationFormBody(trialBookingData)
    recaptchaAPI.reset()

    formBody['recaptchaResponse'] = recaptchaKey
    const countryFromIPOtpCaptcha = parseCookies()?.[
      USER_COUNTRY_FROM_IP_COOKIE
    ]
    if (countryFromIPOtpCaptcha)
      formBody['country'] = countryFromIPOtpCaptcha.toUpperCase()
    const loginResponse = await authUtils.requestLoginOtp(formBody)

    const currentQuery = router.query
    if (course) {
      delete currentQuery.course
    }
    const encodedPhone = encodePhoneQueryValue(trialBookingData.phone)
    const query: Query = {
      ...currentQuery,
      ...(effectiveSlug ? { slug: effectiveSlug } : {}),
      ...(encodedPhone ? { ph: encodedPhone } : {}),
      source,
      ...(course ? { course } : {}),
      ...(utmSource ? { utm_source: utmSource } : {}),
    }

    // Check if user already exists with same phone number
    if (loginResponse.status === 200) {
      trackMixpanelEvent(
        MixpanelEvent.REGISTER_EXISTING_USER_FOUND,
        getMixpanelFormProperties()
      )
      // Will verify OTP on the same page
      if (openOtpVerificationModal) setIsOtpVerificationModalOpen(true)
      else {
        if (next) query.next = next
        router.push({
          pathname: '/login/verify-otp/',
          query,
        })
      }
    } else if (loginResponse.status === 204) {
      changeTrialBookingData({ loading: false })
      setActiveRecaptchaFlow(() => 'signup')
      await recaptchaAPI.execute()
    } else if (loginResponse.status === 406) {
      trackSignupFailed('recaptcha_failed', loginResponse.status)
      changeTrialBookingData({
        errors: { token: [t`Error verifying reCAPTCHA, please try again`] },
        loading: false,
      })
    } else {
      const error = await loginResponse.json()
      trackSignupFailed('otp_request_failed', loginResponse.status, error)
      changeTrialBookingData({
        errors: error,
        loading: false,
      })
    }
  }

  // handle form submit here
  const handleTrialBookingFormSubmit = async (
    e: FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault()

    if (trialBookingData.loading || !recaptchaAPI) return

    if (!LOGGEDIN_PROMO_PAGE) {
      trackMixpanelEvent(
        MixpanelEvent.REGISTER_CLICK_PROCEED,
        getMixpanelFormProperties()
      )
    }

    if (isCountryBlockedForSignup()) {
      trackMixpanelEvent(
        MixpanelEvent.REGISTER_VIEW_ERROR_COUNTRY_BLOCKED,
        getMixpanelFormProperties()
      )
      const commonUtils = await import('@common/utils')
      commonUtils.showErrorAlert({
        text: t`Signup is not available in your region. Please contact support if you think this is a mistake.`,
      })
      return
    }

    const currentQuery = router.query

    if (LOGGEDIN_PROMO_PAGE) {
      delete currentQuery.next
      router.push({
        pathname: next,
        query: currentQuery,
      })
      return
    }

    const validators = await import('@containers/book-trial-class/validators')
    const authUtils = await import('@containers/auth/utils')

    changeTrialBookingData({
      loading: true,
    })

    // Validate client side form inputs,and if any return early
    const errors = validators.validateTrialBookingFormData(trialBookingData)
    const hasSubjectError = subjectDropdown && !selectedSubjectSlug
    if (hasSubjectError) setSubjectError('Please select a subject')
    if (Object.keys(errors).length || hasSubjectError) {
      trackMixpanelEvent(MixpanelEvent.REGISTER_VIEW_ERROR_INVALID_FORM, {
        ...getMixpanelFormProperties(),
        // Field names only (e.g. "phone,childgrade"), never the values.
        fields: [
          ...Object.keys(errors),
          ...(hasSubjectError ? ['subject'] : []),
        ].join(','),
      })
      changeTrialBookingData({
        errors,
        loading: false,
      })
      return
    }

    const formBody = authUtils.prepareRegistrationFormBody(trialBookingData)
    const countryFromIPOtp = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    if (countryFromIPOtp) formBody['country'] = countryFromIPOtp.toUpperCase()
    trackMixpanelEvent(
      MixpanelEvent.REGISTER_DETAILS_SUBMITTED,
      getMixpanelFormProperties()
    )
    let isFailureTracked = false
    try {
      const loginResponse = await authUtils.requestLoginOtp(formBody)
      // if exiting user, go the OTP verification
      if (loginResponse.status === 200) {
        trackMixpanelEvent(
          MixpanelEvent.REGISTER_EXISTING_USER_FOUND,
          getMixpanelFormProperties()
        )
        // Will verify OTP on the same page
        if (openOtpVerificationModal) setIsOtpVerificationModalOpen(true)
        else {
          if (course) {
            delete currentQuery.course
          }
          const encodedPhone = encodePhoneQueryValue(trialBookingData.phone)
          const query: Query = {
            ...currentQuery,
            ...(effectiveSlug ? { slug: effectiveSlug } : {}),
            ...(encodedPhone ? { ph: encodedPhone } : {}),
            source,
            ...(course ? { course } : {}),
            ...(utmSource ? { utm_source: utmSource } : {}),
          }
          if (next) query.next = next

          router.push({
            pathname: '/login/verify-otp/',
            query,
          })
        }
      } else if (loginResponse.status === 204) {
        // If user closes the recaptcha challenge, the CTA is stuck in
        // loading state. Setting loading to false prevents this.
        changeTrialBookingData({ loading: false })
        // if new user, execute the sign up reCAPTCHA
        setActiveRecaptchaFlow(() => 'signup')
        await recaptchaAPI.execute()
      } else if (loginResponse.status === 429) {
        // If user closes the recaptcha challenge, the CTA is stuck in
        // loading state. Setting loading to false prevents this.
        changeTrialBookingData({ loading: false })
        // if OTP generate rate limit (10r/h) is hit, execute OTP generate reCAPTCHA
        setActiveRecaptchaFlow(() => 'login')
        await recaptchaAPI.execute()
      } else if (loginResponse.status === 400) {
        const error = await loginResponse.json()
        trackSignupFailed('invalid_input', loginResponse.status, error)
        changeTrialBookingData({
          errors: error,
          loading: false,
        })
      } else {
        trackSignupFailed('otp_request_failed', loginResponse.status)
        isFailureTracked = true
        const utilsCommon = await import('@lib/utils/common')
        throw new Error(
          await utilsCommon.getAPIErrorString(
            loginResponse,
            loginResponse.url,
            formBody
          )
        )
      }

      // catch any other login errors here
    } catch (error) {
      const commonUtils = await import('@common/utils')
      commonUtils.showErrorAlert({
        text: t`There was an internal error. Please try again later or contact support if the error persists.`,
      })
      const Sentry = await import('@sentry/node')
      Sentry.captureException(error)
      // Network errors and crashes; API errors were tracked with their status.
      if (!isFailureTracked) trackSignupFailed('unexpected_error')
    }
  }

  // handle user creation reCAPTCHA trigger here
  const handleSignupCaptcha = async (recaptchaKey: string) => {
    // the reCAPTCHA was expired then return early
    if (!recaptchaKey) return

    const authUtils = await import('@containers/auth/utils')

    const formBody = {
      ...authUtils.prepareRegistrationFormBody(trialBookingData),
      recaptchaResponse: recaptchaKey,
    }

    recaptchaAPI.reset()

    const signupResponse = await authUtils.registerUser(formBody)

    // if register is successful
    // On successful registration auth token is set
    if (signupResponse.status === 201) {
      const data: UserProps = await signupResponse.json()
      const signupClickIDs = generateClickIDParametersDict(router)
      trackMixpanelSignupCompleted(data, {
        ...getMixpanelFormProperties(),
        // eslint-disable-next-line @typescript-eslint/camelcase
        has_gclid: Boolean(signupClickIDs?.gclid),
        // eslint-disable-next-line @typescript-eslint/camelcase
        has_fbclid: Boolean(signupClickIDs?.fbclid),
      })
      if (window.fbq) {
        window.fbq(
          'trackCustom',
          FB_EVENTS.SIGNUP_COMPLETED,
          {},
          { eventID: data?.id?.toString() }
        )
        track(
          SegmentEventEnum.CONVERSION_SIGNUP_COMPLETED,
          getSegmentTrackingDict(data)
        )
      }

      const normalizedEmail = normalizeEmailForTracking(data?.email)
      // A placeholder number is not the parent's, so never upload it as an
      // enhanced-conversion identifier - it can only ever fail to match.
      const trackablePhone = isPlaceholderPhone(data?.phone)
        ? ''
        : data?.phone || trialBookingData.phone
      const normalizedPhone = normalizePhoneForTracking(trackablePhone)
      const clickIDs = generateClickIDParametersDict(router)
      // Prepare transaction ID for Google Ads conversion tracking.
      // The same transaction ID is used in api/apps/analytics/tasks.py
      const transactionId = data?.id
        ? `student_registration_${data.id}`
        : undefined

      // Google Ads conversion tracking for student registration
      window?.gtag?.('event', 'conversion', {
        ['send_to']: GoogleAdsConversions.STUDENT_REGISTERED,
        ...(transactionId && { ['transaction_id']: transactionId }),
        ...(normalizedEmail && { ['email']: normalizedEmail }),
        ...(normalizedPhone && { ['phone_number']: normalizedPhone }),
        ...(data?.id && { ['user_id']: data.id.toString() }),
        ...(data?.signupSource && { ['signup_source']: data.signupSource }),
        ...(data?.country && { ['country']: data.country }),
        ...(data?.city && { ['city']: data.city }),
        ...(data?.leadSource?.utmSource && {
          ['utm_source']: data.leadSource.utmSource,
        }),
        ...(data?.leadSource?.utmMedium && {
          ['utm_medium']: data.leadSource.utmMedium,
        }),
        ...(data?.leadSource?.utmCampaign && {
          ['utm_campaign']: data.leadSource.utmCampaign,
        }),
        ...(clickIDs?.gclid && { ['gclid']: clickIDs.gclid }),
        ...(clickIDs?.fbclid && { ['fbclid']: clickIDs.fbclid }),
      })

      // GA4 event tracking for student registration
      // It helps track visits to signup and monitor new student registrations in GA4 for funnel analysis and conversion tracking.

      window?.gtag?.('event', 'student_registered_web')

      // In CE-862 we removed OTP verification, so calling this here after token is set.
      handleOtpVerified && (await handleOtpVerified())

      const currentQuery = router.query
      if (course) {
        delete currentQuery.course
      }
      const encodedPhone = encodePhoneQueryValue(trialBookingData.phone)
      const query: Query = {
        ...currentQuery,
        ...(effectiveSlug ? { slug: effectiveSlug } : {}),
        ...(encodedPhone ? { ph: encodedPhone } : {}),
        source,
        ...(course ? { course } : {}),
        ...(utmSource ? { utm_source: utmSource } : {}),
      }
      if (next) {
        delete query.next
        router.push({ pathname: next, query })
        return
      }

      delete query?.ph

      router.push({
        pathname: '/dashboard/',
        query,
      })
      changeTrialBookingData({
        loading: false,
      })
      // If user creation rate limit (10r/h) is hit, show the rate limit error modal
    } else if (signupResponse.status === 429) {
      trackSignupFailed('rate_limited', signupResponse.status)
      setShowRatelimitModal(true)
      changeTrialBookingData({
        loading: false,
      })
      // The user has been blocked, show blocked alert
    } else if (signupResponse.status === 403) {
      trackSignupFailed('blocked', signupResponse.status)
      const commonUtils = await import('@common/utils')
      commonUtils.showErrorAlert({
        text: t`You have been blocked. Please contact support if you think it is a mistake.`,
      })
      changeTrialBookingData({
        loading: false,
      })
      // If any other register errors then handle here
    } else {
      const error = await signupResponse.json()
      trackSignupFailed('registration_failed', signupResponse.status, error)
      changeTrialBookingData({
        errors: error,
        loading: false,
      })
    }
  }

  /** ReCaptcha's onChange function. Triggered when recaptchaAPI.execute() is called. */
  const handleRecaptchaOnChange = async (recaptchaKey: string) => {
    changeTrialBookingData({ loading: true })
    activeRecaptchaFlow === 'signup'
      ? await handleSignupCaptcha(recaptchaKey)
      : await handleOtpRequestCaptcha(recaptchaKey)
  }

  useEffect(() => {
    // Logged-in visitors on promo pages skip signup, so they are not in the
    // signup funnel.
    if (!router.isReady || loggedIn) return
    trackMixpanelEvent(
      MixpanelEvent.REGISTER_VISIT,
      getMixpanelFormProperties()
    )
  }, [router.isReady])

  useEffect(() => {
    if (!router.isReady) return

    const phoneFromQueryParams = router?.query?.ph || router?.query?.phone
    const gradeFromQueryParams = router?.query?.grade
    setIsAutoRegister(router?.query?.autoRegister ? true : false)

    if (phoneFromQueryParams) {
      const decodedPhone = decodePhoneQueryValue(phoneFromQueryParams as string)
      if (decodedPhone) {
        handlePhoneInputChange(decodedPhone, detectIdentifierMode(decodedPhone))
      }
    }

    if (
      gradeFromQueryParams &&
      GRADES.some((grade) => grade.value === gradeFromQueryParams)
    ) {
      changeTrialBookingData({
        childGrade: {
          value: gradeFromQueryParams,
          label: gradeFromQueryParams, // Assuming the label is the same as value in this context
        },
      })
    } else {
      setIsAutoRegister(false)
    }

    // Honor an explicit laptop answer passed in the URL (e.g. from the
    // web-static landing pages, which ask the question and forward it as
    // ?hasLaptop=true|false). When present it is the user's real answer and
    // wins over the device-based default seeded in prepareRegistrationFormBody.
    const hasLaptopFromQueryParams = router?.query?.hasLaptop
    if (
      hasLaptopFromQueryParams === 'true' ||
      hasLaptopFromQueryParams === 'false'
    ) {
      changeTrialBookingData({ hasLaptop: hasLaptopFromQueryParams === 'true' })
    }

    // The web-static landing pages are served from static.codingal.com, so the
    // localStorage signup source the on-site CTAs write never reaches this page
    // and we would otherwise record everyone from an ad as '/register/'. Those
    // pages forward their own full path as ?signupSource=/lp/<page>.html instead.
    const signupSourceFromQueryParams = getValidatedSignupSourceFromQuery(
      router?.query?.signupSource
    )
    if (signupSourceFromQueryParams) {
      changeTrialBookingData({ signupSource: signupSourceFromQueryParams })
    }
  }, [router.isReady])

  const handleGradeSelect = (grade: GradeType) => {
    changeTrialBookingData({ childGrade: grade })
  }

  return (
    <>
      <LazyRecaptcha
        onChange={handleRecaptchaOnChange}
        setRecaptchaAPI={setRecaptchaAPI}
        shouldRecaptchaLoad={shouldRecaptchaLoad}
      />
      <BookTrialClassForm
        {...trialBookingData}
        allowEmail={allowEmail}
        handlePhoneInputChange={handlePhoneInputChange}
        handleSelectChange={handleSelectChange}
        // handleCheckboxChange={handleCheckboxChange}
        handleTrialBookingFormSubmit={handleTrialBookingFormSubmit}
        LOGGEDIN_PROMO_PAGE={LOGGEDIN_PROMO_PAGE}
        ctaText={cta}
        termsAndConditions={termsAndConditions}
        countryCode={countryCode}
        shouldHaveFormShadow={shouldHaveFormShadow}
        formHeading={formHeading}
        phoneFieldLabel={phoneFieldLabel}
        isRightToLeftPage={isRightToLeftPage}
        handleGradeSelect={handleGradeSelect}
        isAutoRegister={isAutoRegister}
        trialSubject={
          subjectDropdown
            ? selectedSubjectSlug
              ? getTrialSubject(selectedSubjectSlug)
              : undefined
            : trialSubject
        }
        subjectDropdown={subjectDropdown}
        subjectOptions={SUBJECT_OPTIONS}
        onSubjectChange={handleSubjectChange}
        subjectError={subjectError}
      />
      <RatelimitErrorModal
        isOpen={showRatelimitModal}
        setIsOpen={setShowRatelimitModal}
      />
      <AuthModal
        phone={trialBookingData.phone}
        isOpen={isOtpVerificationModalOpen}
        setIsOpen={setIsOtpVerificationModalOpen}
        handleOtpVerified={handleOtpVerified}
        redirectionUrl={next}
      />
    </>
  )
}

export default BookTrialClassContainer
